import { Suspense, useEffect } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Outlet, useLocation } from 'react-router-dom';

import type { BadgeKey } from '../app/module-registry';
import { usePrefetchOnLogin } from '../app/usePrefetch';
import { usePermissions } from '../auth/usePermissions';
import { DEV_AUTH_ENABLED } from '../auth/dev-auth';
import { leadsService, paymentsService } from '../services';
import { PageSkeleton } from '../ui/primitives';
import { Sidebar } from './Sidebar';
import { TopBar } from './TopBar';
import { useSidebarState } from './useSidebarState';
import { CACHE } from '../app/query-config';

export function AppShell(): JSX.Element {
  const { collapsed, toggleCollapsed, isMobile, drawerOpen, openDrawer, closeDrawer } =
    useSidebarState();
  const location = useLocation();
  const { allows } = usePermissions();

  /**
   * WARM THE LIKELY-NEXT SCREENS, HERE, ONCE.
   *
   * The shell is the first thing that renders with a live session — it is
   * inside `RequireAuth`, so a token exists by definition — and it survives
   * every navigation, so this runs once per login rather than once per route.
   * See `usePrefetch.ts` for what is warmed and why the list is short.
   */
  usePrefetchOnLogin();

  /*
   * The notification tray's query used to be declared here as well, purely so
   * the sidebar could read `chrome.badges` off it. Those badges are real data
   * now (below) and no longer come from that call, so the duplicate is gone —
   * `TopBar` owns the one subscription, which is where "Mark all read" writes
   * the cache and therefore where it has to be read.
   */

  // Navigating with the drawer open should not leave it covering the page the
  // user just asked for.
  useEffect(() => {
    closeDrawer();
  }, [location.pathname, closeDrawer]);

  /**
   * SIDEBAR COUNTER PILLS — DERIVED FROM REAL ROWS.
   *
   * These were `{ leads: 12, payments: 3 }`, hardcoded in `shellService`: the
   * last of the invented-data fixtures, and against the current database it
   * claimed twelve leads where there are none.
   *
   * No new endpoint was built for them — that is the mistake a previous change
   * had to undo. They reuse two endpoints that already exist, under the SAME
   * query keys the Leads and Payments screens use, so react-query serves both
   * from one request and the pill can never disagree with the screen it points
   * at. It also means the pill IS the prefetch: warming the badge warms the
   * screen.
   *
   * Each is gated on the permission its screen requires. A role that cannot
   * read leads never fires the query — and never sees the pill, because the nav
   * item it hangs on is already filtered out for exactly the same reason.
   */
  const { data: leads } = useQuery({
    queryKey: ['leads', 'list'],
    queryFn: ({ signal }) => leadsService.list(signal),
    ...CACHE.workList,
    enabled: allows('LEAD_CRM', 'VIEW'),
  });

  const { data: paymentSummary } = useQuery({
    queryKey: ['payments', 'summary'],
    queryFn: ({ signal }) => paymentsService.summary(signal),
    ...CACHE.money,
    enabled: allows('PAYMENTS_RECEIPTS', 'VIEW'),
  });

  /**
   * WHAT THE NUMBERS MEAN, stated because a bare count on a nav item is
   * otherwise unreadable:
   *
   *  leads    — leads still awaiting action (NEW or CONTACTED). QUOTED is with
   *             the customer, CONVERTED and LOST are finished; counting those
   *             would make the pill a table size rather than a to-do count.
   *  payments — bookings with an overdue balance, straight from the server's
   *             own `overdueBookingCount` (Booking.total/paid/
   *             nextInstalmentDueDate, not Invoice). Not "payments
   *             received": a counter badge marks what needs chasing, not
   *             what already went well.
   *
   * `undefined` rather than `0` while loading or when not permitted — the
   * sidebar renders a pill only for a count greater than zero, so an absent
   * badge is correctly indistinguishable from "nothing to chase", and no pill
   * ever flashes a wrong number on the way to the right one.
   */
  const badges: Partial<Record<BadgeKey, number>> = {
    leads: leads?.filter((l) => l.stage === 'NEW' || l.stage === 'CONTACTED').length,
    payments: paymentSummary?.overdueBookingCount,
  };

  return (
    // `data-print-root` unwinds the flex/overflow containment for printing, and
    // `data-print-hide` on the chrome keeps the nav off the paper — see the
    // @media print block in index.css.
    <div data-print-root className="flex h-full overflow-hidden bg-canvas">
      {!isMobile && (
        <div data-print-hide className="contents">
          <Sidebar collapsed={collapsed} onToggleCollapsed={toggleCollapsed} badges={badges} />
        </div>
      )}

      {isMobile && drawerOpen && (
        <>
          <div
            className="fixed inset-0 z-40 bg-neutral-900/45"
            onClick={closeDrawer}
            aria-hidden="true"
          />
          <div className="fixed inset-y-0 left-0 z-50 animate-gy-in-fast shadow-lg">
            <Sidebar
              collapsed={false}
              onToggleCollapsed={toggleCollapsed}
              badges={badges}
              asDrawer
              onNavigate={closeDrawer}
            />
          </div>
        </>
      )}

      <div className="flex min-w-0 flex-1 flex-col">
        {DEV_AUTH_ENABLED && (
          // Deliberately unmissable. A fake session that looks real is the one
          // way this stub could cause harm.
          <div
            data-print-hide
            className="flex-none bg-warning px-4 py-1.5 text-center text-[11px] font-bold uppercase leading-none tracking-caps text-ink"
          >
            Dev stub session — no backend, no database, permissions are not enforced
          </div>
        )}
        <div data-print-hide className="contents">
          <TopBar onOpenDrawer={openDrawer} isMobile={isMobile} />
        </div>
        <main className="min-h-0 flex-1 overflow-y-auto">
          {/* Route elements are lazy — this boundary covers the chunk fetch. */}
          <Suspense fallback={<PageSkeleton />}>
            <Outlet />
          </Suspense>
        </main>
      </div>
    </div>
  );
}
